import React from "react";
import { Link } from "react-router-dom";
import { styles } from "../../layout";
import file from "../blog/components/site/file.png";
import github from "../blog/components/site/github.png";
import net from "../blog/components/site/net.png";
import Figure from "../components/figure";
import CustomLink from "../components/hoverlink";

const WebsiteTutorialBlog: React.FC = () => {
  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.header}>How to Build and Deploy a Website</h1>

      <p style={styles.paragraph}>
        Hello! I wanted to make a tutorial on how to make a website and get it
        up and running on the internet. I have always been fascinated with
        creating websites from scratch and it took me much longer than it should
        have to teach myself how to code and create websites.
      </p>

      <p style={styles.paragraph}>
        I wanted to create this blog to show you how to use my free website
        template and create your first simple project (a Link-tree clone) to get
        familiar with the codebase.
      </p>

      <h2 style={styles.sectionTitle}>What We’ll Cover</h2>
      <ul style={styles.list}>
        <li style={styles.listItem}>
          1.
          <CustomLink href="#installation-and-setup">
            Installation and Setup
          </CustomLink>
        </li>
        <li style={styles.listItem}>
          2.
          <CustomLink href="#creating-a-page">Creating a Page</CustomLink>
        </li>
        <li style={styles.listItem}>
          3.
          <CustomLink href="#deploying-to-the-web">
            Deploying to the Web
          </CustomLink>
        </li>
      </ul>

      <h2 id="installation-and-setup" style={styles.sectionTitle}>
        Installation and Setup
      </h2>
      <p style={styles.paragraph}>
        To create and deploy software you need a few things to make the process
        much easier. Most software developers use Git to track version control
        and organize committing changes. For that we will use Github Desktop has
        a simple interface.
      </p>
      <CustomLink
        href="https://desktop.github.com/download/"
        target="_blank"
        rel="noreferrer"
      >
        Github Desktop
      </CustomLink>

      <p style={styles.paragraph}>
        We will also use VSCode as an IDE (Integrated Development Environment).
        This is like the difference of type on a sticky note vs Google Docs. One
        allows you to type and the other is jam packed with tools that make the
        process much easier.
      </p>
      <CustomLink
        href="https://code.visualstudio.com/"
        target="_blank"
        rel="noreferrer"
      >
        VSCode
      </CustomLink>

      <p style={styles.paragraph}>
        Finally, we will use Netlify to deploy our site live. It is a free
        service that makes it much easier to put websites live on the web. We
        will get more into this in the final step.
      </p>
      <CustomLink
        href="https://www.netlify.com/"
        target="_blank"
        rel="noreferrer"
      >
        VSCode
      </CustomLink>

      <p style={styles.paragraph}>
        To use the template for this site open github desktop and clone the repo
        from the URL. Then you are ready to code in VSCode.
      </p>

      <Figure
        imageSrc={github}
        altText="Clone repository in GitHub Desktop"
        caption="Clone repository in GitHub Desktop"
        size="small"
      />

      <h2 id="creating-a-page" style={styles.sectionTitle}>
        Creating a Page
      </h2>
      <p style={styles.paragraph}>
        As mentioned above we will create a page so that we can learn the
        codebase together. A useful page might be a Linktree Style Landing Page
        to host all of your links.
      </p>
      <p style={styles.paragraph}>
        To get started lets map out what we need to do:
      </p>

      <ul style={styles.list}>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Collect all our links</span>
        </li>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Get all our assets into the project</span>
        </li>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Code the page layout</span>
        </li>
      </ul>

      <p style={styles.paragraph}>
        So for this Linktree we will get links to the following:
      </p>

      <ul style={styles.list}>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Youtube</span>
        </li>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Instagram</span>
        </li>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Twitter/X</span>
        </li>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Linkedin</span>
        </li>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <span>Website</span>
        </li>
      </ul>

      <p style={styles.paragraph}>Below are links to images we can use:</p>
      <CustomLink
        href="https://drive.google.com/drive/folders/1S_Cy2QEN0AEO1XhPXv7Y2cknZsTCypZI?usp=sharing"
        target="_blank"
        rel="noreferrer"
      >
        Google Drive Folder with Social Icons
      </CustomLink>

      <p style={styles.paragraph}>
        So I use a bunch of AI to code pages like this. Here is a prompt to
        create a basic page.
      </p>
      <div style={styles.note}>
        <p>
          Hello I am creating a Linktree clone for my personal website. I want a
          single page called
          <code> links.tsx </code> that displays five social media buttons:
          YouTube, Instagram, Twitter, LinkedIn, and Website. Each button should
          have a small icon on the left side and then the button with text. I
          have already imported the images. Please create one page links.tsx.
          Here is a sample pages code for the styling.
        </p>

        <p style={styles.paragraph}>
          <strong>The buttons should:</strong>
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Be centered vertically in a column</li>
          <li style={styles.listItem}>
            Use light styling with padding, border, and hover effect
          </li>
          <li style={styles.listItem}>
            Use icons that I have already imported as image files
          </li>
        </ul>

        <p style={styles.paragraph}>
          <strong>The images are:</strong>
        </p>
        <pre
          style={{
            background: "#f4f4f4",
            padding: "12px",
            borderRadius: "6px",
            overflowX: "auto",
          }}
        >
          <code>
            {`import insta from "./assets/insta.png";
import linkedin from "./assets/linkedin.png";
import website from "./assets/website.png";
import twitter from "./assets/x.png";
import youtube from "./assets/yt.png";`}
          </code>
        </pre>
      </div>

      <p style={styles.paragraph}>
        Then, I created a new file named <code>links.tsx</code> inside the{" "}
        <code>projects</code> folder and add the images to <code>assets</code>{" "}
        folder to import.
      </p>
      <Figure
        imageSrc={file}
        altText="Website file structure"
        caption="Website file structure."
        size="small"
      />

      <p style={styles.paragraph}>
        Finally, I updated <code>app.tsx</code> for routing and added links to
        the header and projects page.
      </p>

      <p style={styles.paragraph}>
        Run the code to make sure everything works.
      </p>

      <h2 id="deploying-to-the-web" style={styles.sectionTitle}>
        Deploying to the Web
      </h2>
      <p style={styles.paragraph}>
        The first step is to commit the code to our github repo. Github desktop
        makes this much easier, so download that and sign in if you have not
        already. Just select the branch you want to commit to, and click push.
        This will commit your changes to github.
      </p>

      <p style={styles.paragraph}>
        Then to deploy our code to a domain we will use Netlify. You will need
        to create an account or link your account with GitHub. Then select your
        repo and deploy it.
      </p>

      <Figure
        imageSrc={net}
        altText="Screenshot of Netlify deployment setup"
        caption="Deploying your site with Netlify."
        size="medium"
      />

      <p style={styles.paragraph}>
        It is also very simple to add a custom domain so feel free to add that
        if you have purchases one.
      </p>

      <p style={styles.paragraph}>
        That is it! Once it is built, your website is live. You can view the
        template at this url
      </p>

      <CustomLink
        href="https://jp-website-template.netlify.app/"
        target="_blank"
        rel="noreferrer"
      >
        jp-website-template.netlify.app
      </CustomLink>

      <p style={styles.paragraph}>
        I hope you learned something and found this valuable!
      </p>

      <p>- Jack</p>
      <p style={styles.date}>Published on July 7, 2025</p>
      <Link to="/blogs" style={styles.backLink}>
        Back to Blogs
      </Link>
    </div>
  );
};

export default WebsiteTutorialBlog;
